// Vendor libraries
import cloneDeep from 'lodash.clonedeep';
import { forkJoin } from 'rxjs';
import { map, mergeMap, retry, shareReplay } from 'rxjs/operators';
// Constants
import { SECURITYLIST_MENU_ORDER_MAP } from 'SecurityListsApp/ts/constants';
import { COMPANY, FUND, INDEX, INDICATOR, MODEL_PORTFOLIO, SECURITYLIST_GROUP_MULTI, } from 'NewAngular/constants/core.constants';
// Services
import { AjaxService } from 'NewAngular/services/core/ajax.service';
import { StaticFileUrlService } from 'NewAngular/services/static-file-url.service';
import { UrlsService } from 'NewAngular/services/core/urls.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import * as i0 from "@angular/core";
import * as i1 from "NewAngular/services/core/ajax.service";
import * as i2 from "NewAngular/services/static-file-url.service";
import * as i3 from "NewAngular/services/core/urls.service";
import * as i4 from "EntitlementsApp/ts/features/feature-needed/feature.service";
export class SecurityListMenuService {
    constructor(ajaxService, staticFileUrlService, urlsService, featureService) {
        this.ajaxService = ajaxService;
        this.staticFileUrlService = staticFileUrlService;
        this.urlsService = urlsService;
        this.featureService = featureService;
        this.STATIC_MENU_URL = 'securities/securitylist_menu.json';
        this.STATIC_LABELS_URL = 'securities/securitylist_menu_labels.json';
        /**
         * Filter category based on always_display and feature_needed properties.
         * @param category Category object being analyzed for filtering.
         * @private boolean True if we want to keep the category, false otherwise.
         */
        this.filterMenuCategory = (category) => {
            // Display element if
            //     always_display is true
            //     or user has the required feature_needed
            if (category.always_display)
                return true;
            return this.featureService.userHasFeatures([category.feature_needed]);
        };
        /**
         * Fetch and cache the menu and labels. Due to the use of `shareReplay`,
         * multiple subscriberes will not trigger additional HTTP requests.
         */
        this.cachedSecurityListMenu$ = this.fetchInitialMenu();
        this.cachedSecurityListLabels$ = this.fetchInitialLabels();
    }
    /**
     * Give a URL for a json file, get the absolute URL to that file and retrieve the json data.
     * @param url: string that is the url for the desired security list menu json file, e.g. 'securities/securitylist_menu.json'
     */
    getStaticData(url) {
        return this.staticFileUrlService.getAbsoluteURL(url).pipe(mergeMap((cachedUrl) => {
            return this.ajaxService.getDataObserveBody(cachedUrl, undefined, {
                'Cache-Control': 'max-age=31536000', // Cache for 1 year
            });
        }));
    }
    getDynamicMenu() {
        return this.ajaxService.getDataObserveBody(this.urlsService.SECURITY_LISTS.DYNAMIC_MENU);
    }
    getDynamicLabels() {
        return this.ajaxService.getDataObserveBody(this.urlsService.SECURITY_LISTS.DYNAMIC_LABELS);
    }
    /**
     * Creates a deep copy of the menu and sorts the categories within each security type of the SecurityListMenu.
     * @param menu SecurityListMenu to sort
     * @returns SecurityListMenu that is a copy of the param menu with categories sorted according to SECURITYLIST_MENU_ORDER_MAP
     */
    sortMenu(menu) {
        // Deep copy the menu
        const menuCopy = cloneDeep(menu);
        Object.keys(menuCopy).forEach((securityType) => {
            const categoryOrder = SECURITYLIST_MENU_ORDER_MAP[securityType] ?? [];
            menuCopy[securityType].sort((a, b) => {
                const aIndex = categoryOrder.indexOf(a.name);
                const bIndex = categoryOrder.indexOf(b.name);
                if (aIndex === -1 || bIndex === -1) {
                    return 0;
                }
                if (aIndex > bIndex) {
                    return 1;
                }
                if (bIndex > aIndex) {
                    return -1;
                }
                return 0;
            });
        });
        return menuCopy;
    }
    /**
     * Filters the categories within each security type of the SecurityListMenu.
     * @param menu SecurityListMenu to sort
     * @returns SecurityListMenu that is a copy of the param menu with categories sorted according to SECURITYLIST_MENU_ORDER_MAP
     */
    filterMenu(menu) {
        return {
            [COMPANY]: menu[COMPANY].filter(this.filterMenuCategory),
            [FUND]: menu[FUND].filter(this.filterMenuCategory),
            [INDEX]: menu[INDEX].filter(this.filterMenuCategory),
            [INDICATOR]: menu[INDICATOR].filter(this.filterMenuCategory),
            [MODEL_PORTFOLIO]: menu[MODEL_PORTFOLIO].filter(this.filterMenuCategory),
            [SECURITYLIST_GROUP_MULTI]: menu[SECURITYLIST_GROUP_MULTI].filter(this.filterMenuCategory),
        };
    }
    /**
     * Gets the security list menu data and puts the categories in the order specified by SECURITYLIST_MENU_ORDER_MAP
     */
    fetchInitialMenu() {
        // forkJoin() makes parallel requests. The next() callback is run when all requests have completed
        return forkJoin([this.getStaticData(this.STATIC_MENU_URL), this.getDynamicMenu()]).pipe(
        // shareReplay allows multiple subscribers to subscribe to this observable
        // without triggering additional HTTP requests.
        shareReplay(1), 
        // If we fail to get the static file manifest retry twice.
        retry(2), map(([staticMenu, dynamicMenu]) => {
            const staticMenuMulti = staticMenu.multi || [];
            const combinedMenu = {
                multi: staticMenuMulti.concat(dynamicMenu.multi),
                company: staticMenu.company.concat(dynamicMenu.company),
                fund: staticMenu.fund.concat(dynamicMenu.fund),
                index: staticMenu.index.concat(dynamicMenu.index),
                indicator: staticMenu.indicator.concat(dynamicMenu.indicator),
                model_portfolio: staticMenu.model_portfolio.concat(dynamicMenu.model_portfolio),
            };
            const filteredMenu = this.filterMenu(combinedMenu);
            return this.sortMenu(filteredMenu);
        }));
    }
    fetchInitialLabels() {
        // forkJoin() makes parallel requests. The next() callback is run when all requests have completed
        return forkJoin([this.getStaticData(this.STATIC_LABELS_URL), this.getDynamicLabels()]).pipe(
        // shareReplay allows multiple subscribers to subscribe to this observable
        // without triggering additional HTTP requests.
        shareReplay(1), 
        // If we fail to get the static file manifest retry twice.
        retry(2), map(([staticLabels, dynamicLabels]) => {
            // Merge static and dynamic labels
            return {
                multi: dynamicLabels.multi, // There are no static, user-generated lists
                company: Object.assign(staticLabels.company, dynamicLabels.company),
                fund: Object.assign(staticLabels.fund, dynamicLabels.fund),
                index: Object.assign(staticLabels.index, dynamicLabels.index),
                indicator: Object.assign(staticLabels.indicator, dynamicLabels.indicator),
            };
        }));
    }
    getMenu() {
        return this.cachedSecurityListMenu$;
    }
    getLabels() {
        return this.cachedSecurityListLabels$;
    }
}
SecurityListMenuService.ɵfac = function SecurityListMenuService_Factory(t) { return new (t || SecurityListMenuService)(i0.ɵɵinject(i1.AjaxService), i0.ɵɵinject(i2.StaticFileUrlService), i0.ɵɵinject(i3.UrlsService), i0.ɵɵinject(i4.FeatureService)); };
SecurityListMenuService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SecurityListMenuService, factory: SecurityListMenuService.ɵfac, providedIn: 'root' });
